import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { planService } from "../../api";
import { useAlert } from "../../context/AlertContext";
import {
  getCountryOptions,
  getCurrencyForCountryOption,
  getDodoCurrencyOptions,
  isDodoSupportedCurrency,
  normalizeCountryLabel as normalize,
} from "../../utils/countryCurrency";

const COUNTRY_OPTIONS = getCountryOptions(); // [{ value: "AE", label: "United Arab Emirates" }, ...]
const CURRENCY_OPTIONS = getDodoCurrencyOptions();

// Per-country price management for a plan (edit mode only — needs a plan id).
// Uses POST /plan/:id/pricing (assign/update) and DELETE /plan/:id/pricing/:country
// (soft-delete). Existing prices are editable inline; a country that already has
// an active price cannot be added again (edit it instead).
const PlanCountryPricing = ({ planId }) => {
  const { showAlert } = useAlert();
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState("");
  const [syncWarning, setSyncWarning] = useState("");

  // Add-new row (shown inside the table when adding === true). `country` holds
  // the full { value: isoCode, label } option — the ISO code resolves the
  // currency; only the label is ever sent to the backend.
  const [adding, setAdding] = useState(false);
  const [country, setCountry] = useState(null);
  const [currency, setCurrency] = useState("");
  const [price, setPrice] = useState("");

  // Inline edit of an existing row.
  const [editingCountry, setEditingCountry] = useState("");
  const [editCurrency, setEditCurrency] = useState("");
  const [editPrice, setEditPrice] = useState("");

  const load = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    try {
      const res = await planService.getById(planId);
      const plan = res?.data || res || {};
      setPricing(Array.isArray(plan?.pricing) ? plan.pricing : []);
    } catch (e) {
      /* leave as-is */
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    load();
  }, [load]);

  // Only countries with an active price are "configured".
  const activePricing = useMemo(
    () => pricing.filter((p) => p?.isActive === true),
    [pricing]
  );

  // A country that already has an active price can't be added again.
  const availableCountryOptions = useMemo(() => {
    const used = new Set(activePricing.map((p) => normalize(p.country)));
    return COUNTRY_OPTIONS.filter((c) => !used.has(normalize(c.label)));
  }, [activePricing]);

  // assign/update handles both create and edit (same upsert endpoint).
  const savePricing = async (payload, { onDone } = {}) => {
    setSyncWarning("");
    try {
      const res = await planService.assignPricing(planId, {
        country: payload.country,
        currency: String(payload.currency).toUpperCase(),
        price: Number(payload.price),
      });
      if (res?.success === false) {
        showAlert(res?.message || "Could not save the price.", "error");
        return false;
      }
      // Mongo write succeeded — but the Dodo sync may have failed independently.
      const sync = res?.data?.syncResult;
      if (sync && sync.success === false) {
        setSyncWarning(
          `Price saved, but it is NOT live on checkout yet: ${sync.reason || "Dodo sync failed."}`
        );
      } else {
        showAlert("Country price saved.", "success");
      }
      if (onDone) onDone();
      load();
      return true;
    } catch (e) {
      showAlert(e?.message || "Could not save the price.", "error");
      return false;
    }
  };

  const currencyUnsupported = currency && !isDodoSupportedCurrency(currency);

  const handleCountryChange = (option) => {
    setCountry(option);
    // Auto-fill currency from the country's ISO code. If it can't be resolved
    // (no world-countries entry) or isn't Dodo-supported, leave it blank so the
    // admin picks one manually from the dropdown below instead of being blocked.
    const mapped = option ? getCurrencyForCountryOption(option) : "";
    setCurrency(mapped && isDodoSupportedCurrency(mapped) ? mapped : "");
  };

  const handleAdd = async () => {
    if (!country || String(price).trim() === "") {
      showAlert("Country, currency and price are all required.", "error");
      return;
    }
    if (!currency) {
      showAlert("Couldn't determine a currency for this country — please select one.", "error");
      return;
    }
    if (currencyUnsupported) {
      showAlert(
        `${currency} isn't supported by the payment provider, so ${country.label} can't be charged.`,
        "error"
      );
      return;
    }
    setSaving(true);
    await savePricing(
      { country: country.label, currency, price },
      { onDone: resetAddRow }
    );
    setSaving(false);
  };

  const resetAddRow = () => {
    setAdding(false);
    setCountry(null);
    setCurrency("");
    setPrice("");
  };

  const startAdd = () => {
    cancelEdit();
    setAdding(true);
    setCountry(null);
    setCurrency("");
    setPrice("");
  };

  const startEdit = (entry) => {
    setEditingCountry(entry.country);
    setEditCurrency(String(entry.currency || "").toUpperCase());
    setEditPrice(String(entry.price ?? ""));
    setSyncWarning("");
  };

  const cancelEdit = () => {
    setEditingCountry("");
    setEditCurrency("");
    setEditPrice("");
  };

  const handleSaveEdit = async (entryCountry) => {
    if (!editCurrency || String(editPrice).trim() === "") {
      showAlert("Currency and price are required.", "error");
      return;
    }
    setSaving(true);
    const ok = await savePricing(
      { country: entryCountry, currency: editCurrency, price: editPrice },
      { onDone: cancelEdit }
    );
    if (!ok) setSaving(false);
    else setSaving(false);
  };

  const handleDelete = async (entryCountry) => {
    setDeleting(entryCountry);
    try {
      const res = await planService.removePricing(planId, entryCountry);
      if (res?.success === false) {
        showAlert(res?.message || "Could not remove the price.", "error");
        return;
      }
      showAlert("Country price removed.", "success");
      load();
    } catch (e) {
      showAlert(e?.message || "Could not remove the price.", "error");
    } finally {
      setDeleting("");
    }
  };

  if (!planId) return null;

  return (
    <Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 0.5 }}>
        Country Pricing
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#878787", mb: 1.5 }}>
        Each country has its own localized price, editable here. Prices render to
        customers exactly as entered — no conversion.
      </Typography>

      {syncWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSyncWarning("")}>
          {syncWarning}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={startAdd}
          disabled={loading || adding || !!editingCountry}
          sx={{ textTransform: "none" }}
        >
          Add country
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : activePricing.length === 0 && !adding ? (
        <Typography sx={{ fontSize: "13px", color: "#878787", mb: 2 }}>
          No active country prices yet.
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 2, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "40%" }}>Country</TableCell>
              <TableCell sx={{ width: "20%" }}>Currency</TableCell>
              <TableCell align="right" sx={{ width: "20%" }}>Price</TableCell>
              <TableCell align="right" sx={{ width: "20%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activePricing.map((p) => {
              const isEditing = normalize(editingCountry) === normalize(p?.country);
              return (
                <TableRow key={p?.country}>
                  <TableCell>{p?.country}</TableCell>
                  {/* Currency is not editable on an existing row — it's fixed
                      once the price was created. */}
                  <TableCell>{String(p?.currency || "").toUpperCase()}</TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editPrice}
                        onChange={(e) => setEditPrice(e.target.value)}
                        sx={{ width: "100%" }}
                        inputProps={{ min: 0, style: { textAlign: "right" } }}
                      />
                    ) : (
                      p?.price
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <>
                        <IconButton
                          size="small"
                          disabled={saving}
                          onClick={() => handleSaveEdit(p?.country)}
                        >
                          <CheckIcon fontSize="small" color="success" />
                        </IconButton>
                        <IconButton size="small" disabled={saving} onClick={cancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          disabled={!!editingCountry || adding || deleting === p?.country}
                          onClick={() => startEdit(p)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={!!editingCountry || adding || deleting === p?.country}
                          onClick={() => handleDelete(p?.country)}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* New editable row */}
            {adding && (
              <TableRow>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={availableCountryOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, val) => option?.value === val?.value}
                    value={country}
                    onChange={(e, v) => handleCountryChange(v)}
                    sx={{ width: "100%" }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select country" />
                    )}
                  />
                </TableCell>
                {/* Currency auto-fills from the country when possible; the admin
                    can always override it, so an unresolved auto-fill never
                    blocks adding a price. */}
                <TableCell>
                  <Select
                    size="small"
                    displayEmpty
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    sx={{ width: "100%" }}
                    data-testid="pricing-currency-select"
                  >
                    <MenuItem value="" disabled>Select currency</MenuItem>
                    {CURRENCY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                  {currencyUnsupported && (
                    <Typography sx={{ fontSize: "11px", color: "#d32f2f", lineHeight: 1.2 }}>
                      Not supported by payment provider
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    sx={{ width: "100%" }}
                    inputProps={{ min: 0, style: { textAlign: "right" } }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    disabled={saving || currencyUnsupported}
                    onClick={handleAdd}
                  >
                    <CheckIcon fontSize="small" color="success" />
                  </IconButton>
                  <IconButton size="small" disabled={saving} onClick={resetAddRow}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default PlanCountryPricing;
